import { useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { ApiHelper, USER_ROLE_PRODUCER } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import useDialog from "@datanac/datanac-mui-toolkit/dist/hooks/useDialog";
import { DeleteForeverOutlined } from "@mui/icons-material";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import CurrencyExchangeOutlined from "@mui/icons-material/CurrencyExchangeOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EastOutlined from "@mui/icons-material/EastOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { _sessionManager, AipPassApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import DataFormSelect from "components/Core/DataFormSelect";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { EditIcon, MemberManagementIcon } from "components/Icons/Icons";
import { useBlockerPrompt } from "components/Menu/hooks/useBlockerPrompt";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { USER_ROLE_ADMIN, USER_ROLE_AGENT } from "core/useSession";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import { formatNumber, formatPersonName, formatPrice } from "../../utility/formatters";
import { MASK_NUMERIC } from "../Core/StatefulControlledTextField";
import { ClientAccountsList } from "../Marketing/ClientAccountsList";
import AgentUserGrantDataTable from "./components/AgentUserGrantDataTable";
import { getErrorMessage } from "./components/helpers";
import { InvoiceItemDialog } from "./components/InvoiceItemDialog";
import MemberUserDataTable from "./components/MemberUserDataTable";
import { NewGroupDialog } from "./components/NewGroupDialog";
import { NewMemberDialog } from "./components/NewMemberDialog";
import { NewUserDialog } from "./components/NewUserDialog";
import { ProducerPolicyDialog } from "./components/ProducerPolicyDialog";
import ProducerSearchDialog from "./components/ProducerSearchDialog";

export default function MemberAccountWorkspace() {
  const { globalState } = useContext(AppContext);

  const { member_id } = useParams();
  const navigate = useNavigate();
  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();

  const { enqueueSnackbar } = useSnackbar();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });

  const USER_ROLE_LENDER = "agent/lender";

  // --- --- ---

  const paymentTypes = [
    { payment_type: "STRIPE", name: "Stripe Payment" },
    { payment_type: "PAPER_CHECK", name: "Paper Check" },
  ];

  const [isLoadingSubscriptionTypes, setIsLoadingSubscriptionTypes] = useState(true);
  const [subscriptionTypes, setSubscriptionTypes] = useState(null);
  const [addOnTypes, setAddOnTypes] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [editingInvoiceItem, setEditingInvoiceItem] = useState(null);

  const handleError = (err) => {
    console.warn(err);
    const errorMessage = getErrorMessage(err);
    enqueueSnackbar(errorMessage, { preventDuplicate: true, variant: "error" });
  };

  const loadSubscriptionTypes = async () => {
    try {
      const _subscriptionTypes = await UsersApiHelper.users_selectObject("SubscriptionType", { is_active: true }, {});
      if (_subscriptionTypes?.length) {
        const _orderedSubscriptionTypes = _subscriptionTypes
          .filter((x) => !x.is_add_on)
          .sort((a, b) => a.subscription_type?.localeCompare(b.subscription_type));
        const _orderedAddOnTypes = _subscriptionTypes
          .filter((x) => x.is_add_on)
          .sort((a, b) => a.subscription_type?.localeCompare(b.subscription_type));
        setSubscriptionTypes(_orderedSubscriptionTypes);
        setAddOnTypes(_orderedAddOnTypes);
      } else {
        enqueueSnackbar("Could not load Subscription Types. Please check the billing service and try again.", {
          preventDuplicate: true,
          variant: "error",
        });
      }

      setIsLoadingSubscriptionTypes(false);
    } catch (err) {
      setIsLoadingSubscriptionTypes(false);
      setSubscriptionTypes(null);
      setAddOnTypes(null);

      handleError(err);
    }
  };

  useEffect(() => {
    loadSubscriptionTypes();
  }, [globalState]);

  // --- --- ---

  const [isLoadingAgentTokens, setIsLoadingAgentTokens] = useState(true);
  const [agentTokens, setAgentTokens] = useState(null);

  const loadAgentTokens = async () => {
    try {
      const _agentTokens = await AipPassApiHelper.pass_selectObject("AgentTokens", {}, {});
      if (_agentTokens?.length) {
        const _orderedAgentTokens = _(_agentTokens)
          .uniqBy((agent) => agent.last_name + ", " + agent.first_name)
          .value()
          .sort((a, b) => a.last_name?.localeCompare(b.last_name) || a.first_name?.localeCompare(b.first_name));
        setAgentTokens(_orderedAgentTokens);
      } else {
        enqueueSnackbar("Could not load Agents. Please check the connection and try again.", {
          preventDuplicate: true,
          variant: "error",
        });
      }

      setIsLoadingAgentTokens(false);
    } catch (err) {
      setIsLoadingAgentTokens(false);
      setAgentTokens(null);

      handleError(err);
    }
  };

  useEffect(() => {
    loadAgentTokens();
  }, [globalState]);

  // --- --- ---

  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingMember, setIsUpdatingMember] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [member, dispatchMember] = useReducer(propUpdateReducer, {
    country_code: "US",
    is_active: true,
    payment_type: "STRIPE",
  });

  const loadMember = async (_memberID) => {
    try {
      const _memberFilter = {
        id: _memberID || member_id,
      };
      const members = await UsersApiHelper.users_selectObject("Member", _memberFilter, {});
      if (members?.length) {
        dispatchMember({
          type: "updateMany",
          payload: members[0],
        });
      }

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      dispatchMember({
        type: "replace",
        payload: {},
      });

      handleError(err);
    }
  };

  useEffect(() => {
    if (member_id && String(member_id) !== "0") {
      loadMember();
    } else if (String(member_id) === "0" && !member?.id) {
      const _newMemberID = uuidv4();
      dispatchMember({
        type: "update",
        payload: { key: "id", value: _newMemberID },
      });

      setIsLoading(false);
    }
  }, [globalState, member?.id, member_id]);

  const isMemberCreated = useMemo(() => {
    return !isLoading && String(member_id) !== "0" && !!member?.createdate;
  }, [member?.createdate, member_id, isLoading]);

  // ---

  useEffect(() => {
    if (isMemberCreated) return;

    if (member?.user_role === USER_ROLE_PRODUCER) {
      const _producer_token = member?.producer_token || uuidv4();
      dispatchMember({
        type: "destructure",
        payload: {
          my_agent_token: "",
          producer_token: _producer_token,
        },
      });
    } else if (member?.user_role === USER_ROLE_AGENT || member?.user_role === USER_ROLE_LENDER) {
      // Agent/Lender:
      if (!member?.my_agent_token) {
        dispatchMember({
          type: "destructure",
          payload: {
            my_agent_token: member?.id,
            agent_token: member?.id,
            producer_token: "",
          },
        });
      }
    }
  }, [
    member?.user_role,
    member?.id,
    member?.agent_token,
    member?.producer_token,
    member?.my_agent_token,
    isMemberCreated,
  ]);

  useEffect(() => {
    // Opening existing record:
    if (member?.id && member?.producer_token) {
      dispatchMember({
        type: "updateMany",
        payload: {
          user_role: USER_ROLE_PRODUCER,
        },
      });
    } else if (member?.id && !member?.producer_token) {
      dispatchMember({
        type: "updateMany",
        payload: {
          my_agent_token: member?.id,
          user_role: USER_ROLE_AGENT,
        },
      });
    }
  }, [member?.producer_token, member?.id, isMemberCreated]);

  // ---

  const isLockedField = (fieldName) => {
    return isMemberCreated && ["user_role", "producer_token", "my_agent_token", "agent_token"].includes(fieldName);
  };

  const handleChangeState = (event, value) => {
    if (isLockedField(event.target.name)) {
      return;
    }

    if (value == null && event.target.value != null) {
      value = event.target.value;
    }

    setIsDirty(true);

    dispatchMember({
      type: "update",
      payload: { key: event.target.name, value: value },
    });
  };

  const handleChangeStateSelect = (event) => {
    if (isLockedField(event.target.name)) {
      return;
    }

    setIsDirty(true);

    dispatchMember({
      type: "update",
      payload: { key: event.target.name, value: event.target.value },
    });
  };

  const handleChangeStateCheckbox = (event) => {
    if (isLockedField(event?.target?.id)) {
      return;
    }

    setIsDirty(true);

    dispatchMember({
      type: "update",
      payload: { key: event?.target?.id, value: event?.target?.checked },
    });
  };

  // --- --- ---

  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [users, setUsers] = useState(null);

  const loadUsers = async () => {
    try {
      const _usersFilter = {
        member_id: member?.id,
        is_active: true,
      };
      const _users = await UsersApiHelper.users_selectObject("User", _usersFilter, {});
      if (_users?.length) {
        setUsers(_users);
      } else {
        setUsers(null);
      }

      setIsLoadingUsers(false);
    } catch (err) {
      setIsLoadingUsers(false);
      setUsers(null);

      handleError(err);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [users?.length, member?.id]);

  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [groups, setGroups] = useState(null);
  const loadGroups = async () => {
    try {
      const group_results = [];
      for (const user of users || []) {
        try {
          const api = new ApiHelper({
            baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
            config: {},
            sessionManager: _sessionManager,
            _instance: null,
          });
          const user_name = encodeURIComponent(user?.email);
          // Make API POST request
          const _groups = await api.callPost(`Member/group/search?user_name=${user_name}`, {}, globalState);

          if (_groups?.length) {
            _groups.forEach((group, index) => {
              group_results.push({ id: `${user.email}-${index}`, UserName: user.email, GroupName: group });
            });
          }
        } catch (innerErr) {
          console.error(`Error processing user ${user?.email}:`, innerErr);
          continue;
        }
      }

      setGroups(group_results);
      setIsLoadingGroups(false);
    } catch (err) {
      setIsLoadingGroups(false);
      setGroups(null);
      handleError(err);
    }
  };
  const handleRowDelete_Group = useCallback(
    async (row) => {
      confirmDialogActions.confirm({
        title: "Delete this entry?",
        prompt: "Are you sure you want to delete this entry?",
        promptDetail: null,
        callback: () => {
          _handleRowDeleteIntern_Group(row);
        },
      });
    },
    [confirmDialogActions]
  );

  const _handleRowDeleteIntern_Group = async (row) => {
    const _row = { ...row };
    _row.is_active = false;

    removeGroup(_row);
    loadGroups();
  };

  const removeGroup = async (row) => {
    try {
      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null,
      });

      // Make API POST request
      await api.callPost("Member/group/remove", { UserName: row.UserName, GroupName: row.GroupName }, globalState);
    } catch (err) {
      handleError(err);
    }
  };

  const group_columns = useMemo(
    () => [
      {
        field: "UserName",
        headerName: "Username",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "GroupName",
        headerName: "Group Name",
        minWidth: 150,
        flex: 1,
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteForeverOutlined />}
            label="Delete"
            onClick={() => handleRowDelete_Group(params?.row)}
          />,
        ],
        flex: 1,
      },
    ],
    []
  );

  useEffect(() => {
    if (member?.id) {
      loadUsers();
    }
  }, [globalState, member?.id, member_id]);

  // ---

  const [userGrants, setUserGrants] = useState([]);
  const loadUserGrants = async () => {
    try {
      const _userGrants = await UsersApiHelper.selectUserGrants(
        {
          user_id: member?.id,
          agent_token: member?.id,
          is_active: true,
        },
        {}
      );
      setUserGrants(_userGrants);
    } catch (err) {
      setIsLoading(false);
      setUserGrants([]);

      handleError(err);
    }
  };

  useEffect(() => {
    if (member?.id && (member?.user_role == USER_ROLE_AGENT || member?.user_role == USER_ROLE_LENDER)) {
      loadUserGrants();
    }
  }, [globalState, member?.user_role, member?.id]);

  // ---

  useEffect(() => {
    loadPolicies();
  }, [member?.producer_token]);

  const [isLoadingPolicies, setIsLoadingPolicies] = useState(true);
  const [policies, setPolicies] = useState(null);
  const loadPolicies = async () => {
    try {
      if (!member?.producer_token) {
        return;
      }

      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null,
      });

      const _policies = await api.callPost(
        `ProducerPolicy/Search`,
        { producer_token: member?.producer_token, is_active: true },
        globalState
      );

      setPolicies(_policies);
      setIsLoadingPolicies(false);
    } catch (err) {
      setIsLoadingPolicies(false);
      setPolicies(null);
      handleError(err);
    }
  };
  const handleRowDelete_Policy = useCallback(
    async (row) => {
      confirmDialogActions.confirm({
        title: "Delete this entry?",
        prompt: "Are you sure you want to delete this entry?",
        promptDetail: null,
        callback: () => {
          _handleRowDeleteIntern_Policy(row);
        },
      });
    },
    [confirmDialogActions]
  );

  const _handleRowDeleteIntern_Policy = async (row) => {
    const _row = { ...row };
    _row.is_active = false;

    await removePolicy(_row);
    loadPolicies();
  };

  const removePolicy = async (row) => {
    try {
      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null,
      });

      await api.callPost("ProducerPolicy", row, globalState);
    } catch (err) {
      handleError(err);
    }
  };

  const policy_columns = useMemo(
    () => [
      {
        field: "aip_code",
        headerName: "AIP Code",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "policy_number",
        headerName: "Policy Number",
        minWidth: 150,
        flex: 1,
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) => [
          <>
            <GridActionsCellItem
              key="edit"
              icon={<EditIcon />}
              label="Edit"
              onClick={() => setPolicyDialog(params?.row)}
            />
            <GridActionsCellItem
              key="delete"
              icon={<DeleteForeverOutlined />}
              label="Delete"
              onClick={() => handleRowDelete_Policy(params?.row)}
            />
          </>,
        ],
        flex: 1,
      },
    ],
    [policies]
  );

  // --- --- ---
  const isValid = useMemo(() => {
    if (!member) return false;

    const hasName = (member.last_name && member.first_name) || member.business_name;
    const hasContact = member.email && member.phone;
    const hasAddress = member.address1 && member.city && member.state_abbreviation && member.zip;
    const hasBilling = (member.subscription_type && member.payment_type) || member.subscription_is_posted === true;

    const hasToken = Boolean(member?.producer_token || member?.my_agent_token);

    return !!(hasName && hasContact && hasAddress && hasBilling && hasToken);
  }, [
    member?.last_name,
    member?.first_name,
    member?.business_name,
    member?.email,
    member?.phone,
    member?.address1,
    member?.city,
    member?.state_abbreviation,
    member?.zip,
    member?.subscription_type,
    member?.payment_type,
    member?.subscription_is_posted,
    member?.producer_token,
    member?.my_agent_token,
  ]);

  useEffect(() => {
    if (isValid && member?.id && member?.user_role && !isUpdatingMember && isDirty) {
      setIsUpdatingMember(true);
      const _newState = { ...member };

      if (member?.user_role != USER_ROLE_PRODUCER) {
        _newState.agent_token = member?.my_agent_token;
        _newState.producer_token = null;
      } else {
        _newState.agent_token = null;
      }

      // This is an admin function.
      // Do not pass globalState to updateMember (producer_token).
      UsersApiHelper.updateMember(_newState, {}).finally(() => {
        setIsUpdatingMember(false);
        setIsDirty(false);
      });
    }
  }, [member, isValid, isMemberCreated, isDirty]);

  // --- --- ---

  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState(null);
  useEffect(() => {
    if (subscriptionTypes?.length && member?.subscription_type) {
      const _foundSubscriptionType = subscriptionTypes?.find((t) => t.subscription_type == member?.subscription_type);
      if (_foundSubscriptionType) {
        setSelectedSubscriptionType(_foundSubscriptionType);
      } else {
        setSelectedSubscriptionType(null);
      }
    }
  }, [globalState, subscriptionTypes, member?.subscription_type]);

  // --- --- ---

  const [isProducerSearchDialogOpen, setIsProducerSearchDialogOpen] = useState(false);

  const handleProducerSearchItemSelect = async (currentProducer) => {
    if (isMemberCreated) return;
    if (currentProducer?.producer_token) {
      setIsDirty(true);
      dispatchMember({
        type: "update",
        payload: { key: "producer_token", value: currentProducer?.producer_token },
      });
    }
  };

  // --- --- ---

  const [isNewGroupDialogOpen, setIsNewGroupDialogOpen] = useState(false);
  const handleAddNewGroupClick = async () => {
    setIsNewGroupDialogOpen(true);
  };

  const [isNewUserDialogOpen, setIsNewUserDialogOpen] = useState(false);
  const handleAddNewUserClick = async () => {
    setIsNewUserDialogOpen(true);
  };

  const [isNewMemberDialogOpen, setIsNewMemberDialogOpen] = useState(false);
  const handleAddNewMemberClick = async () => {
    setIsNewMemberDialogOpen(true);
  };

  const [policyDialog, setPolicyDialog] = useState(null);
  const handleAddNewPolicyClick = async () => {
    setPolicyDialog({
      producer_token: member?.producer_token,
      is_active: true,
      id: uuidv4(),
    });
  };

  const [isInvoiceItemDialogOpen, setIsInvoiceItemDialogOpen] = useState(false);
  const handleAddInvoiceItemClick = async () => {
    setEditingInvoiceItem(null);
    setIsInvoiceItemDialogOpen(true);
  };
  const handleEditInvoiceItemClick = async (editingInvoiceItem) => {
    setEditingInvoiceItem(editingInvoiceItem);
    setIsInvoiceItemDialogOpen(true);
  };
  const handleDeleteInvoiceItemClick = async (editingInvoiceItem) => {
    setInvoiceItems((prev) => prev.filter((l) => l.id !== editingInvoiceItem.id));
  };
  const handleAddInvoiceItem = (invoiceItem) => {
    if (editingInvoiceItem) {
      setInvoiceItems((prev) => prev.map((l) => (l.id === editingInvoiceItem.id ? { ...invoiceItem, id: l.id } : l)));
    } else {
      setInvoiceItems((prev) => [...prev, { ...invoiceItem, id: crypto.randomUUID() }]);
    }
    setIsInvoiceItemDialogOpen(false);
    setEditingInvoiceItem(null);
  };

  const [isPostingTransaction, setIsPostingTransaction] = useState(false);

  const FREE_OVERRIDE_DEFAULT_VALUE = 950;

  // --- --- ---

  const formMaxWidthShort = "800px";

  useBlockerPrompt(!isValid, "Member");

  const validationErrors = useMemo(() => {
    const _validationErrors = [];
    if (!isLoading) {
      if (!isValid) {
        _validationErrors.push("Please enter all required fields");
      }
      if (member.fee_override) {
        const overrideValue = parseFloat(member.fee_override);
        if (isNaN(overrideValue)) {
          _validationErrors.push("Fee Override must be a valid number");
        } else if (overrideValue < FREE_OVERRIDE_DEFAULT_VALUE) {
          _validationErrors.push(`Fee Override must be at least $${FREE_OVERRIDE_DEFAULT_VALUE}`);
        }
      }
    }
    return _validationErrors;
  }, [member, isLoading, isValid]);

  useEffect(() => {
    if (isValid) {
      const _description = member?.business_name ? member?.business_name : formatPersonName(member);
      dispatchMember({
        type: "update",
        payload: { key: "description", value: _description },
      });
    }
  }, [member?.last_name, member?.first_name, member?.middle_name, member?.business_name, member?.id, isValid]);

  // ---

  const [tax, setTax] = useState(null);
  const [isLoadingTax, setIsLoadingTax] = useState(false);

  const effectiveSubtotal = useMemo(() => {
    const override = member.fee_override;
    if (override && override !== "") {
      const parsed = parseFloat(override);
      if (!isNaN(parsed) && parsed >= FREE_OVERRIDE_DEFAULT_VALUE) {
        return parsed;
      }
    }
    return parseFloat(selectedSubscriptionType?.subtotal) || null;
  }, [member.fee_override, selectedSubscriptionType]);

  const addOnSubtotal = useMemo(() => {
    return (invoiceItems || []).reduce((sum, item) => {
      const unitPrice = parseFloat(item.price_override ?? item.subtotal ?? 0) || 0;
      const qty = parseFloat(item.quantity) || 0;
      const minimum = parseFloat(item.amount_minimum ?? 0) || 0;
      return sum + Math.max(unitPrice * qty, minimum);
    }, 0);
  }, [invoiceItems]);

  // Add-ons-only mode: posted + has add-ons → show/calculate only add-ons, not base subscription
  // When posted with NO add-ons, keep base subscription info so the Payment Link still works for unpaid members
  const addOnsOnlyMode = Boolean(member?.subscription_is_posted) && (invoiceItems || []).length > 0;

  const loadTax = async () => {
    setIsLoadingTax(true);
    try {
      const subtotal = effectiveSubtotal;
      if (subtotal !== null) {
        // Calculate tax separately per line (base + each add-on), then sum — matching the API's Submit Transaction behavior
        // Skip base subscription tax only when in add-ons-only mode (posted + has add-ons)
        // When posted with no add-ons, keep base tax so the billing summary reflects the unpaid subscription
        const baseRequest = addOnsOnlyMode
          ? Promise.resolve(0)
          : UsersApiHelper.selectMemberSubscriptionTaxAmount({ ...member, subtotal }, globalState);

        const addOnRequests = (invoiceItems || []).map((item) => {
          const unitPrice = item.price_override ?? item.subtotal ?? 0;
          const qty = parseFloat(item.quantity) || 0;
          const minimum = item.amount_minimum ?? 0;
          const lineSubtotal = Math.max(unitPrice * qty, minimum);
          return UsersApiHelper.selectMemberSubscriptionTaxAmount({ ...member, subtotal: lineSubtotal }, globalState);
        });

        const taxResults = await Promise.all([baseRequest, ...addOnRequests]);
        const totalTax = taxResults.reduce((sum, t) => sum + (parseFloat(t) || 0), 0);
        setTax(totalTax);
      } else {
        setTax(0.0);
      }
    } catch (err) {
      setTax(0.0);
      handleError(err);
    } finally {
      setIsLoadingTax(false);
    }
  };

  useEffect(() => {
    if (
      member?.country_code &&
      member?.address1 &&
      member?.city &&
      member?.state_abbreviation &&
      member?.zip &&
      (selectedSubscriptionType || member.fee_override)
    ) {
      loadTax();
    }
  }, [
    member?.country_code,
    member?.address1,
    member?.city,
    member?.state_abbreviation,
    member?.zip,
    selectedSubscriptionType,
    member.fee_override,
    addOnSubtotal,
  ]);

  // ---

  const handleTransactionPostClick = async () => {
    confirmDialogActions.confirm({
      title: "Post transaction?",
      prompt: "Are you sure you want to post this transaction to accounting?",
      promptDetail: null,
      callback: handleTransactionPostClick_Intern,
    });
  };

  const openInNewTab = (url) => {
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };

  const handlePaymentLinkClick = async () => {
    const url = await UsersApiHelper.selectMemberPaymentSessionUrl(
      {
        ...member,
        AddOnLineItems: (invoiceItems || []).map((item) => ({
          subscription_type: item.subscription_type,
          quantity: item.quantity,
          price_override: item.price_override ?? null,
        })),
      },
      globalState
    );
    openInNewTab(url);
  };

  const handleTransactionPostClick_Intern = async () => {
    if (isValid && member?.id && validationErrors.length === 0) {
      const _newState = { ...member };

      if (member?.user_role != USER_ROLE_PRODUCER) {
        _newState.agent_token = member?.my_agent_token;
      }

      _newState.AddOnLineItems = (invoiceItems || []).map((item) => ({
        subscription_type: item.subscription_type,
        quantity: item.quantity,
        price_override: item.price_override ?? null,
      }));

      if (member?.subscription_is_posted) {
        // Subscription already posted — send add-on lines only; skip re-posting the primary subscription.
        _newState.add_ons_only = true;
      }
      // First-time post: keep subscription_is_posted false in the payload. The API uses it to decide
      // whether to post the base subscription sale; it sets subscription_is_posted true when persisting.
      // Sending true here made new members skip the base sale (only add-on lines would post).

      setIsPostingTransaction(true);
      try {
        // This is an admin function.
        // Do not pass globalState to updateMemberSubscription (producer_token).
        await UsersApiHelper.updateMemberSubscription(_newState, {});
        setInvoiceItems([]);
        loadMember(_newState?.id);
        loadUserGrants();
        setIsPostingTransaction(false);
      } catch (err) {
        const errorMessage = err?.detail || err?.message || err;
        if (errorMessage?.includes("UQ_")) {
          console.warn(err);
          enqueueSnackbar(
            "The member name already has a subscription.  Member names must be unique.  Please find the other account or choose a new name.",
            { preventDuplicate: true, variant: "error" }
          );
        } else {
          handleError(err);
        }
        // Leave isPostingTransaction=true on error to keep the button disabled
      }
    }
  };

  const [isLoadingPayments, setIsLoadingPayments] = useState(true);
  const [payments, setPayments] = useState(null);
  const loadPayments = async () => {
    try {
      if (member?.subscription_is_posted && member?.producer_token) {
        const payments = await UsersApiHelper.selectMemberPayments(member, globalState);
        if (payments?.length) {
          setPayments(payments);
          setIsLoadingPayments(false);
        } else {
          setPayments(null);
          setIsLoadingPayments(false);
        }
      } else {
        setPayments(null);
        setIsLoadingPayments(false);
      }
    } catch (err) {
      setIsLoadingPayments(false);
      setPayments(null);

      handleError(err);
    }
  };

  useEffect(() => {
    loadPayments();
  }, [member?.id, member?.subscription_is_posted, member?.producer_token]);

  // --- Danger Area Handlers ---

  const [selectedMFAUser, setSelectedMFAUser] = useState("");

  const handleResetMFA = async () => {
    if (!selectedMFAUser) return;

    confirmDialogActions.confirm({
      title: "Reset MFA Preference?",
      prompt: `Are you sure you want to reset MFA for ${selectedMFAUser}?`,
      promptDetail: "This will disable all MFA settings for this user.",
      callback: async () => {
        try {
          const api = new ApiHelper({
            baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
            config: {},
            sessionManager: _sessionManager,
            _instance: null,
          });
          await api.callPost("Member/ResetMFA", { user_name: selectedMFAUser }, globalState);
          enqueueSnackbar("MFA preference reset successfully", { variant: "success" });
          setSelectedMFAUser("");
          loadUsers();
        } catch (err) {
          handleError(err);
        }
      },
    });
  };

  const handleDeleteUser = async () => {
    confirmDialogActions.confirm({
      title: "Delete Member Account?",
      prompt: "Are you sure you want to delete this member account?",
      promptDetail:
        "This action is NOT reversible. The account will be deactivated and all Broker/Agent relationships will be disabled. All associated users will be deleted from Cognito.",
      callback: async () => {
        try {
          if (users && users.length > 0) {
            const api = new ApiHelper({
              baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
              config: {},
              sessionManager: _sessionManager,
              _instance: null,
            });

            const deletePromises = users.map((user) =>
              api.callPost("Member/login/delete", { user_name: user.email, id: user.id }, globalState)
            );

            await Promise.all(deletePromises);
          }

          const remainingUsers = await UsersApiHelper.users_selectObject(
            "User",
            { member_id: member.id, is_active: true },
            {}
          );
          if (remainingUsers && remainingUsers.length > 0) {
            throw new Error("Unable to delete all associated users. Operation cancelled for safety.");
          }

          const _newState = { ...member, is_active: false };
          await UsersApiHelper.updateMember(_newState, {});

          loadMember(member?.id);
          loadUsers();
          enqueueSnackbar("Member account and associated users deleted successfully", { variant: "success" });
          navigate("/account");
        } catch (err) {
          handleError(err);
        }
      },
    });
  };

  const handleDeleteUserLogin = async (user) => {
    confirmDialogActions.confirm({
      title: "Delete User Login?",
      prompt: `Are you sure you want to delete the login for ${user.email}?`,
      promptDetail:
        "This action is NOT reversible. The user will be permanently deleted from Cognito and deactivated in the system.",
      callback: async () => {
        try {
          const api = new ApiHelper({
            baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
            config: {},
            sessionManager: _sessionManager,
            _instance: null,
          });
          await api.callPost("Member/login/delete", { user_name: user.email, id: user.id }, globalState);
          loadUsers();
        } catch (err) {
          handleError(err);
        }
      },
    });
  };

  const COLUMNS_ADD_ONS = useMemo(
    () => [
      {
        field: "quantity",
        headerName: "Qty",
        flex: 0.5,
        valueFormatter: (value) => (value !== null ? formatNumber(value, 2) : ""),
      },
      {
        headerName: "Unit Price",
        flex: 1,
        valueGetter: (value, row) => row?.price_override ?? row?.subtotal,
        valueFormatter: (value) => (value !== null ? formatPrice(value) : ""),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditOutlinedIcon />}
            label="Edit"
            onClick={() => handleEditInvoiceItemClick(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteOutlinedIcon />}
            label="Delete"
            onClick={() => handleDeleteInvoiceItemClick(params.row)}
          />,
        ],
        flex: 0,
      },
    ],
    [handleEditInvoiceItemClick, handleDeleteInvoiceItemClick]
  );

  return (
    <>
      <WorkspaceHeader
        title={member_id != 0 ? "Manage Member" : "New Member"}
        category={{
          title: "Members",
          icon: <MemberManagementIcon />,
        }}
        breadCrumbs={[
          {
            title: "Members",
            path: "/account",
          },
        ]}
      />

      <List>
        {validationErrors.map((err, index) => (
          <ListItem key={index}>
            <ListItemAvatar>
              <ErrorOutline color="error" />
            </ListItemAvatar>
            <ListItemText>
              <Typography color="error">{err}</Typography>
            </ListItemText>
          </ListItem>
        ))}
      </List>

      <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Typography variant="h5">Name</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="First Name"
                name="first_name"
                isRequired={!member?.business_name?.trim()}
                defaultValue={member?.first_name || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Middle Name"
                name="middle_name"
                isRequired={false}
                defaultValue={member?.middle_name || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Last Name"
                name="last_name"
                isRequired={!member?.business_name?.trim()}
                defaultValue={member?.last_name || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Business Name"
                name="business_name"
                isRequired={false}
                defaultValue={member?.business_name || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Typography variant="h5">Contact</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Email"
                id="member-email"
                name="email"
                isRequired={true}
                defaultValue={member?.email || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Phone"
                name="phone"
                isRequired={true}
                defaultValue={member?.phone || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Address"
                name="address1"
                isRequired={true}
                defaultValue={member?.address1 || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Address 2"
                name="address2"
                isRequired={false}
                defaultValue={member?.address2 || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="City"
                name="city"
                isRequired={true}
                defaultValue={member?.city || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <DataFormSelect
              value={member?.state_abbreviation}
              name="state_abbreviation"
              onChange={handleChangeState}
              objectName="State"
              itemFilter={{ reinsurance_year: getCurrentReinsuranceYear() }}
              itemGetKey={(item) => item.state_abbreviation}
              itemGetLabel={(item) => item.state_abbreviation}
              doFetch={true}
              isLoading={isLoading}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Zip"
                name="zip"
                isRequired={true}
                defaultValue={member?.zip || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
        <Grid item xs={12} lg={6}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <Stack direction="column" spacing={0}>
                <Typography variant="h5">Account</Typography>
                {member?.subscription_is_posted && (
                  <Alert severity="info">
                    <Typography variant="body1">Subscription is submitted. Further changes are not allowed.</Typography>
                  </Alert>
                )}
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <FormLabel id="account-type-label">Account Type</FormLabel>
                <SkeletonWrapper isLoading={isLoadingAgentTokens}>
                  <RadioGroup
                    row
                    defaultValue=""
                    aria-labelledby="account-type-label"
                    name="user_role"
                    value={(() => {
                      if (member?.user_role) {
                        return member?.user_role;
                      } else {
                        if (member?.agent_token) {
                          return USER_ROLE_AGENT;
                        } else {
                          return USER_ROLE_PRODUCER;
                        }
                      }
                    })()}
                    onChange={handleChangeState}
                  >
                    <FormControlLabel
                      value={USER_ROLE_PRODUCER}
                      control={<Radio />}
                      label="Producer"
                      disabled={member?.subscription_is_posted == true || isMemberCreated}
                    />
                    <FormControlLabel
                      value={USER_ROLE_AGENT}
                      control={<Radio />}
                      label="Agent"
                      disabled={member?.subscription_is_posted == true || isMemberCreated}
                    />
                    <FormControlLabel
                      value={USER_ROLE_LENDER}
                      control={<Radio />}
                      label="Lender"
                      disabled={member?.subscription_is_posted == true || isMemberCreated}
                    />
                  </RadioGroup>
                </SkeletonWrapper>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              {member?.user_role == USER_ROLE_PRODUCER && (
                <FormControl fullWidth>
                  <InputLabel id="agent_tokenLabel">Agent</InputLabel>
                  <SkeletonWrapper isLoading={isLoadingAgentTokens}>
                    <Select
                      labelId="agent_tokenLabel"
                      displayEmpty
                      value={(member?.user_role == USER_ROLE_PRODUCER && member?.agent_token) || ""}
                      name={"agent_token"}
                      onChange={handleChangeStateSelect}
                      label={"Agent"}
                      disabled={member?.user_role != USER_ROLE_PRODUCER || member?.subscription_is_posted == true}
                    >
                      {agentTokens?.map((currentItem, index) => {
                        return (
                          <MenuItem key={index} value={currentItem?.agent_token}>
                            {currentItem?.last_name}, {currentItem?.first_name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </SkeletonWrapper>
                </FormControl>
              )}

              {member?.user_role !== USER_ROLE_PRODUCER && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth={true}>
                    <FormControlLabel
                      control={<Checkbox id="do_create_demo_account" onChange={handleChangeStateCheckbox} />}
                      label="Automatically create demo account?"
                    />
                  </FormControl>
                </Grid>
              )}
            </Grid>

            {member?.user_role === USER_ROLE_PRODUCER && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth={true}>
                  <StatefulControlledTextField
                    label="DATANAC Producer Token"
                    name="producer_token"
                    isRequired={false}
                    defaultValue={member?.producer_token || ""}
                    variant="outlined"
                    onChange={handleChangeState}
                    InputLabelProps={{ shrink: true }}
                    isLoading={isLoading}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => setIsProducerSearchDialogOpen(true)}
                          disabled={isMemberCreated}
                        >
                          <Tooltip title="Search for producer">
                            <SearchOutlined />
                          </Tooltip>
                        </IconButton>
                      </InputAdornment>
                    }
                    disabled={isMemberCreated}
                  />
                </FormControl>
              </Grid>
            )}

            {member?.user_role !== USER_ROLE_PRODUCER && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth={true}>
                  <StatefulControlledTextField
                    label="DATANAC Agent Token"
                    name="my_agent_token"
                    isRequired={false}
                    defaultValue={member?.my_agent_token || ""}
                    variant="outlined"
                    onChange={handleChangeState}
                    InputLabelProps={{ shrink: true }}
                    isLoading={isLoading}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => setIsProducerSearchDialogOpen(true)}
                          disabled={isMemberCreated}
                        >
                          <Tooltip title="Search for agent">
                            <SearchOutlined />
                          </Tooltip>
                        </IconButton>
                      </InputAdornment>
                    }
                    disabled={isMemberCreated}
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={12}>
            <Typography variant="h5">Billing</Typography>
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {validationErrors.map((error) => (
                  <div key={error}>{error}</div>
                ))}
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="sales_representative_tokenLabel">Sales Representative</InputLabel>
              <SkeletonWrapper isLoading={isLoadingAgentTokens}>
                <Select
                  labelId="sales_representative_tokenLabel"
                  displayEmpty
                  value={member?.sales_representative || ""}
                  name={"sales_representative"}
                  onChange={handleChangeStateSelect}
                  label={"Sales Representative"}
                  disabled={member?.subscription_is_posted || false}
                >
                  {agentTokens?.map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={currentItem?.agent_token}>
                        {currentItem?.last_name}, {currentItem?.first_name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </SkeletonWrapper>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="subscription_typeLabel">Subscription Type</InputLabel>
              <SkeletonWrapper isLoading={isLoadingSubscriptionTypes}>
                <Select
                  labelId="subscription_typeLabel"
                  displayEmpty
                  value={member?.subscription_type || ""}
                  name={"subscription_type"}
                  onChange={handleChangeStateSelect}
                  label={"Subscription Type"}
                  disabled={member?.subscription_is_posted || false}
                >
                  {subscriptionTypes?.map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={currentItem?.subscription_type}>
                        {currentItem?.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </SkeletonWrapper>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Fee Override"
                name="fee_override"
                isRequired={false}
                defaultValue={member?.fee_override || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                // Fee Override is disabled after transaction is posted to prevent financial inconsistencies
                disabled={member?.subscription_is_posted || false}
                helperText={`Minimum $${FREE_OVERRIDE_DEFAULT_VALUE}`}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container>
              {!addOnsOnlyMode && (
                <>
                  <Grid item xs={6}>
                    Subtotal
                  </Grid>
                  <Grid item xs={6}>
                    <SkeletonWrapper isLoading={isLoadingTax}>
                      <Typography>{formatPrice(effectiveSubtotal ?? 0, 2)}</Typography>
                    </SkeletonWrapper>
                  </Grid>
                </>
              )}

              {addOnSubtotal > 0 && (
                <>
                  <Grid item xs={6}>
                    Add-Ons
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>{formatPrice(addOnSubtotal, 2)}</Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={6} sx={{ borderBottom: "1px solid var(--mui-palette-divider)" }}>
                Tax
              </Grid>
              <Grid item xs={6} sx={{ borderBottom: "1px solid var(--mui-palette-divider)" }}>
                <SkeletonWrapper isLoading={isLoadingTax}>
                  <Typography>{formatPrice(tax ?? 0, 2)}</Typography>
                </SkeletonWrapper>
              </Grid>

              <Grid item xs={6}>
                Total
              </Grid>
              <Grid item xs={6}>
                <SkeletonWrapper isLoading={isLoadingTax}>
                  <Typography>
                    {formatPrice(
                      (addOnsOnlyMode ? 0 : parseFloat(effectiveSubtotal ?? 0) || 0) +
                        parseFloat(addOnSubtotal || 0) +
                        parseFloat(tax ?? 0) || 0,
                      2
                    )}
                  </Typography>
                </SkeletonWrapper>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h5">Add-Ons</Typography>
              </Stack>
              <TilleyDataGrid
                rows={invoiceItems || []}
                columns={COLUMNS_ADD_ONS}
                pageSizeOptions={[5, 10, 25]}
                defaultPageSize={isMobile ? 5 : 10}
                gridKey="account-add-on-lines"
                getRowId={(row) => row.id}
                rowHeight={80}
                isLoading={isLoading}
                sx={{ width: "100%" }}
              />
              {invoiceItems.length > 0 && (
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Add-Ons Subtotal:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatPrice(addOnSubtotal, 2)}
                  </Typography>
                </Stack>
              )}
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="contained" color="primary" onClick={handleAddInvoiceItemClick}>
                  Add
                </Button>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} display="flex" sx={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                disabled={
                  isLoadingTax ||
                  isPostingTransaction ||
                  validationErrors.length > 0 ||
                  (member?.subscription_is_posted ? invoiceItems.length === 0 : !selectedSubscriptionType)
                }
                onClick={handleTransactionPostClick}
              >
                {isPostingTransaction ? (
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                ) : (
                  <CurrencyExchangeOutlined sx={{ mr: 1 }} />
                )}
                {member?.subscription_is_posted && invoiceItems.length > 0 ? "Submit Add-Ons" : "Submit Transaction"}
              </Button>

              <Button
                variant="outlined"
                disabled={isLoadingPayments || Boolean(payments?.length)}
                onClick={handlePaymentLinkClick}
              >
                Payment Link <EastOutlined sx={{ ml: 1 }} />
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
        <ClientAccountsList member={member} onChange={handleChangeState} />
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} alignItems="center" className="headerWithMenuButton">
              <Typography variant="h5">Policies</Typography>

              <Box>
                <Button variant="outlined" sx={{ width: "100%" }} onClick={handleAddNewPolicyClick}>
                  <AddCircleOutline sx={{ mr: 1 }} />
                  Add {!isSmall ? " Policy" : ""}
                </Button>
              </Box>
            </Stack>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <DataGrid
                rows={policies || []}
                columns={policy_columns}
                initialState={{
                  pagination: { paginationModel: { pageSize: isMobile ? 10 : 10 } },
                }}
                pageSizeOptions={[5, 10, 25]}
              />
            </div>
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} alignItems="center" className="headerWithMenuButton">
              <Typography variant="h5">Groups</Typography>

              <Box>
                <Button
                  variant="outlined"
                  sx={{ width: "100%" }}
                  onClick={handleAddNewGroupClick}
                  disabled={!users?.length}
                >
                  <AddCircleOutline sx={{ mr: 1 }} />
                  Add {!isSmall ? " Group" : ""}
                </Button>
              </Box>
            </Stack>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <DataGrid
                rows={groups || []}
                columns={group_columns}
                initialState={{
                  pagination: { paginationModel: { pageSize: isMobile ? 10 : 10 } },
                }}
                pageSizeOptions={[5, 10, 25]}
              />
            </div>
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} alignItems="center" className="headerWithMenuButton">
              <Typography variant="h5">Users</Typography>

              <Box>
                <Button
                  variant="outlined"
                  sx={{ width: "100%" }}
                  onClick={handleAddNewUserClick}
                  disabled={!isValid || isLoadingUsers}
                >
                  <AddCircleOutline sx={{ mr: 1 }} />
                  New {!isSmall ? " User" : ""}
                </Button>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={12}>
            <SkeletonWrapper isLoading={isLoadingUsers}>
              <MemberUserDataTable
                users={users}
                member={member}
                isLoading={isLoadingUsers}
                onDeleteUser={handleDeleteUserLogin}
              />
            </SkeletonWrapper>
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
        <Grid container spacing={2}>
          {member?.user_role === USER_ROLE_AGENT && (
            <>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} alignItems="center" className="headerWithMenuButton">
                  <Typography variant="h5">Members</Typography>

                  <Box>
                    <Button
                      variant="outlined"
                      sx={{ width: "100%" }}
                      onClick={handleAddNewMemberClick}
                      disabled={!isValid || isLoadingUsers}
                    >
                      <AddCircleOutline sx={{ mr: 1 }} />
                      New {!isSmall ? " Member" : ""}
                    </Button>
                  </Box>
                </Stack>
                <AgentUserGrantDataTable userGrants={userGrants} isLoading={isLoading} />
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      <Dialog open={isNewGroupDialogOpen}>
        <NewGroupDialog
          users={users}
          onClose={() => {
            setIsNewGroupDialogOpen(false);
            loadGroups();
          }}
        />
      </Dialog>

      <Dialog open={isNewUserDialogOpen}>
        <NewUserDialog
          member={member}
          onClose={() => {
            setIsNewUserDialogOpen(false);
            loadUsers();
          }}
        />
      </Dialog>

      <Dialog open={isNewMemberDialogOpen}>
        <NewMemberDialog
          member={member}
          onClose={() => {
            setIsNewMemberDialogOpen(false);
            loadUserGrants();
          }}
        />
      </Dialog>

      <Dialog open={isProducerSearchDialogOpen} PaperProps={{ sx: { minWidth: "50%" } }}>
        <ProducerSearchDialog
          onClose={() => {
            setIsProducerSearchDialogOpen(false);
          }}
          onItemSelect={(currentProducer) => {
            handleProducerSearchItemSelect(currentProducer);
            setIsProducerSearchDialogOpen(false);
          }}
        />
      </Dialog>

      <Dialog open={Boolean(policyDialog !== null)}>
        <ProducerPolicyDialog
          currentPolicy={policyDialog}
          onClose={() => {
            setPolicyDialog(null);
            loadPolicies();
          }}
        />
      </Dialog>

      <InvoiceItemDialog
        open={isInvoiceItemDialogOpen}
        onClose={() => setIsInvoiceItemDialogOpen(false)}
        onSave={handleAddInvoiceItem}
        initialValues={editingInvoiceItem || null}
        addOnTypes={addOnTypes || []}
      />

      {/* Danger Area - Admin Only */}
      {globalState?.user_role === USER_ROLE_ADMIN && member?.id && member_id !== "0" && (
        <>
          <Divider sx={{ mt: 4, mb: 2 }} />
          <Box
            sx={{
              width: "100%",
              maxWidth: formMaxWidthShort,
              border: "1px solid",
              borderColor: "error.main",
              borderRadius: 2,
              p: 3,
              backgroundColor: "rgba(211, 47, 47, 0.04)",
            }}
          >
            <Stack spacing={3}>
              <Alert severity="warning">
                These actions are permanent and cannot be undone. Please proceed with caution.
              </Alert>

              <Divider />

              {/* Reset MFA Section */}
              <Box>
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <ShieldOutlined />
                      <Typography variant="h6">Remove MFA Preference</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Reset multi-factor authentication settings for users of this member account. This will disable all
                      MFA preferences.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={2} alignItems="end" flexWrap="wrap">
                    <FormControl sx={{ minWidth: "250px", flex: 1 }}>
                      <InputLabel id="mfa-user-select-label">Select User</InputLabel>
                      <Select
                        labelId="mfa-user-select-label"
                        label="Select User"
                        disabled={!users?.length}
                        value={selectedMFAUser}
                        onChange={(e) => setSelectedMFAUser(e.target.value)}
                      >
                        {users?.map((user, index) => (
                          <MenuItem key={index} value={user.email}>
                            {user.email}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<ShieldOutlined />}
                      onClick={handleResetMFA}
                      disabled={!selectedMFAUser}
                    >
                      Reset MFA
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              <Divider />

              {/* Delete Account Section */}
              <Box>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                  <Box sx={{ flex: 1, minWidth: "200px" }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <DeleteForeverOutlined color="error" />
                      <Typography variant="h6" color="error">
                        Delete Member Account
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Permanently deactivate this member account. This action is NOT reversible and will disable all
                      Broker/Agent relationships.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteForeverOutlined />}
                    onClick={handleDeleteUser}
                    disabled={!member?.is_active}
                  >
                    Delete Account
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </>
      )}

      {globalState?.user_role === USER_ROLE_AGENT && <WorkspaceActionBar content={<></>} />}

      {confirmDialogComponent}
    </>
  );
}
